import { Button, Checkbox, Divider, Form, Input, PlusOutlined, Select, Space } from 'gokwik-ui-kit'
import { Fragment, useEffect, useState } from 'react'
import { getCardLabel, getEmiCodes } from './offer.createEdit.service'
import { OFFER_CATEGORY } from '../constants'

interface BankCodesInputProps {
    banks: Record<'label' | 'value', string>[]
    disabled?: boolean
}

export interface BankRow {
    bank: string
    methods: string[]
    tenures: Record<string, string[]>
}

export interface EmiCode {
    id: string
    mapping_type: string
    payment_provider: string
    method: string
    instrument: string
    gk_code: string
    pg_code: string
    is_active: boolean
    metadata: EmiCodeMetaData
    created_at: string
    updated_at: string
}

export interface EmiCodeMetaData {
    bank_code: string
    tenure_in_months: number
}

export default function BankCodesInput({ banks, disabled }: BankCodesInputProps) {
    const [bankCodes, setBankCodes] = useState<{ label: string; value: string }[]>([])
    const [emiCodes, setEmiCodes] = useState<Record<string, EmiCode[]>>({})
    const form = Form.useFormInstance()

    useEffect(() => {
        const seen = new Set()
        const filteredBanks = banks?.filter((bank) => {
            const duplicate = seen.has(bank.value)
            seen.add(bank.value)
            return !duplicate
        })
        setBankCodes(filteredBanks)
    }, [banks])

    useEffect(() => {
        form?.getFieldValue('valid_bank_codes')?.forEach((bank) => {
            setCodes(bank.bank)
        })
    }, [])

    const setCodes = async (bankCode: string) => {
        if (!bankCode) return

        const codes = await new Promise<EmiCode[]>((resolve) => {
            getEmiCodes(bankCode, (data) => resolve(data || []))
        })

        const seen = new Set()
        const filteredCodes = codes.filter((code) => {
            const duplicate = seen.has(code.gk_code)
            seen.add(code.gk_code)
            return !duplicate
        })

        setEmiCodes((prev) => ({ ...prev, [bankCode]: filteredCodes }))
    }

    const handleBankChange = async (bankCode: string, form: any, name: any) => {
        if (!bankCode) return

        await setCodes(bankCode)

        form.setFieldValue(['valid_bank_codes', name, 'methods'], [])
        form.setFieldValue(['valid_bank_codes', name, 'tenures'], {})
    }

    const handleMethodChange = (methods: string[], form: any, name: any) => {
        const currentTenures = form.getFieldValue(['valid_bank_codes', name, 'tenures']) || {}

        const filteredTenures = Object.fromEntries(
            Object.entries(currentTenures).filter(([method]) => methods.includes(method)),
        )

        form.setFieldValue(['valid_bank_codes', name, 'tenures'], filteredTenures)
    }

    const getAvailableMethods = (bankCode: string) => {
        const codes = emiCodes[bankCode] || []
        const methods = new Set<string>()
        codes.forEach((code) => {
            if (code.metadata?.bank_code === bankCode) {
                methods.add(code.method)
            }
        })
        return Array.from(methods)
    }

    const getAvailableTenures = (bankCode: string, method: string) => {
        const codes = emiCodes[bankCode] || []
        const tenures = new Set<{ tenure: number; code: string }>()
        codes.forEach((code) => {
            if (code.metadata?.bank_code === bankCode && code.method === method) {
                tenures.add({ tenure: code.metadata.tenure_in_months, code: code.gk_code })
            }
        })
        return Array.from(tenures).sort((a, b) => a.tenure - b.tenure)
    }

    return (
        <Form.List
            name='valid_bank_codes'
            rules={[
                {
                    validator: async (_, value) => {
                        if (!value?.length) {
                            return Promise.reject(new Error('Add at least 1 item'))
                        }
                        return Promise.resolve()
                    },
                },
            ]}
        >
            {(fields, { add, remove }, { errors }) => (
                <Form.Item className='mb-0'>
                    <div className='flex flex-col gap-y-2'>
                        {fields.map(({ name, key, ...restField }, i) => (
                            <div key={key} className='flex flex-col items-start gap-y-2'>
                                <div className='flex w-full gap-x-4'>
                                    <Form.Item dependencies={[[name, 'bank']]} noStyle>
                                        {({ getFieldValue }) => (
                                            <>
                                                <Form.Item
                                                    {...restField}
                                                    label='Bank'
                                                    name={[name, 'bank']}
                                                    className='flex-1'
                                                    required
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: 'Bank is required',
                                                        },
                                                    ]}
                                                >
                                                    <Select
                                                        onChange={(value) => handleBankChange(value, form, name)}
                                                        options={bankCodes}
                                                        disabled={disabled}
                                                        placeholder='Select Bank'
                                                        showSearch
                                                    />
                                                </Form.Item>

                                                <Form.Item
                                                    {...restField}
                                                    label='Methods'
                                                    name={[name, 'methods']}
                                                    className='flex-1'
                                                    required
                                                    rules={[
                                                        {
                                                            required: true,
                                                            type: 'array',
                                                            min: 1,
                                                            message: 'A method is required',
                                                        },
                                                    ]}
                                                    dependencies={[[name, 'bank']]}
                                                >
                                                    <Select
                                                        mode='multiple'
                                                        onChange={(value) => handleMethodChange(value, form, name)}
                                                        options={getAvailableMethods(
                                                            getFieldValue(['valid_bank_codes', name, 'bank']),
                                                        ).map((method) => ({
                                                            label: getCardLabel(method),
                                                            value: method,
                                                        }))}
                                                        disabled={
                                                            disabled ||
                                                            !getFieldValue(['valid_bank_codes', name, 'bank'])
                                                        }
                                                        placeholder='Select Methods'
                                                    />
                                                </Form.Item>
                                            </>
                                        )}
                                    </Form.Item>
                                </div>

                                <Form.Item shouldUpdate noStyle>
                                    {({ getFieldValue }) => {
                                        return getFieldValue(['valid_bank_codes', name, 'methods'])?.length ? (
                                            <Space direction='vertical'>
                                                {getFieldValue(['valid_bank_codes', name, 'methods'])
                                                    .sort()
                                                    .map((method: string) => (
                                                        <Space wrap key={method}>
                                                            <Form.Item
                                                                {...restField}
                                                                label={getCardLabel(method) + ' Tenures'}
                                                                name={[name, 'tenures', method]}
                                                                className='w-full'
                                                                required
                                                                rules={[
                                                                    {
                                                                        required: true,
                                                                        type: 'array',
                                                                        min: 1,
                                                                        message: 'A tenure is required',
                                                                    },
                                                                ]}
                                                                dependencies={[[name, 'methods']]}
                                                                valuePropName='value'
                                                            >
                                                                <Checkbox.Group
                                                                    options={getAvailableTenures(
                                                                        getFieldValue([
                                                                            'valid_bank_codes',
                                                                            name,
                                                                            'bank',
                                                                        ]),
                                                                        method,
                                                                    ).map((tenure) => ({
                                                                        label: `${tenure.tenure} months`,
                                                                        value: tenure.code,
                                                                    }))}
                                                                    disabled={disabled}
                                                                />
                                                            </Form.Item>
                                                        </Space>
                                                    ))}
                                            </Space>
                                        ) : null
                                    }}
                                </Form.Item>

                                {length > 1 && !disabled ? (
                                    <Button
                                        className={`ml-auto ${i < fields.length - 1 ? '' : 'mb-4'}`}
                                        danger
                                        onClick={() => remove(name)}
                                    >
                                        Remove
                                    </Button>
                                ) : null}

                                {length > 1 && i < fields.length - 1 ? <Divider dashed /> : null}
                            </div>
                        ))}

                        {!disabled ? (
                            <Button
                                type='dashed'
                                onClick={() => {
                                    add({
                                        bank: undefined,
                                        methods: [],
                                        tenures: {},
                                    } as BankRow)
                                }}
                                block
                                icon={<PlusOutlined />}
                            >
                                Add Bank
                            </Button>
                        ) : null}

                        <Form.ErrorList errors={errors} />
                    </div>
                </Form.Item>
            )}
        </Form.List>
    )
}
